<template>
    <div v-if="showRankingTable" id="public-bracket-ranking" class="columns">
        <div class="column is-full">
            <div class="ranking-snapshot">
                <div class="ranking-snapshot__header mb-3">
                    <span class="icon-text">
                        <span class="icon has-text-primary">
                            <i class="fas fa-trophy"></i>
                        </span>
                        <span class="has-text-weight-semibold is-size-5">
                            {{ translate("page.public_brackets.ranking_snapshot_title") }}
                        </span>
                        <span class="has-text-grey-light is-size-6 ml-1">
                            {{ translate("page.public_brackets.ranking_as_of", false, [rankingDate]) }}
                        </span>
                    </span>
                </div>

                <div class="table-container is-hidden-touch">
                    <table class="table is-fullwidth is-striped is-hoverable ranking-snapshot__table">
                        <thead>
                            <tr>
                                <th class="has-text-grey-light">#</th>
                                <th class="has-text-grey-light">
                                    {{ translate("common.first_name_and_last_name") }}
                                </th>
                                <th class="has-text-grey-light">{{ translate("common.academy") }}</th>
                                <th class="has-text-grey-light has-text-right">
                                    {{ translate("page.public_brackets.ranking_score") }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(entry, index) in rankingEntries"
                                :key="entry.registrationId"
                                :class="{ 'ranking-snapshot__row--protected': entry.protected }"
                            >
                                <td class="has-text-grey ranking-snapshot__position">{{ index + 1 }}</td>
                                <td>
                                    <span class="is-flex is-align-items-center is-gap-1">
                                        <CompetitorAvatar
                                            :first-name="entry.firstName"
                                            :last-name="entry.lastName"
                                            :photo-url="entry.photoUrl"
                                        />
                                        <span class="clarity-mask">{{ entry.firstName }} {{ entry.lastName }}</span>
                                        <span
                                            v-if="entry.protected"
                                            class="tag is-warning is-light is-small ranking-snapshot__badge"
                                            :title="translate('page.public_brackets.ranking_protected_seed')"
                                        >
                                            <i class="fas fa-shield-alt"></i>
                                        </span>
                                    </span>
                                </td>
                                <td class="has-text-grey-light">{{ entry.academy }}</td>
                                <td class="has-text-right">
                                    <span v-if="entry.score !== null" class="has-text-weight-bold has-text-primary">
                                        {{ formatScore(entry.score) }}
                                    </span>
                                    <span v-else class="has-text-grey">-</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="is-hidden-desktop">
                    <div
                        v-for="(entry, index) in rankingEntries"
                        :key="entry.registrationId"
                        class="box has-no-glow-effect ranking-snapshot__mobile-row"
                        :class="{ 'is-primary': entry.protected }"
                    >
                        <div class="is-flex is-align-items-center is-justify-content-space-between">
                            <span class="tag is-dark ranking-snapshot__position">{{ index + 1 }}</span>
                            <span
                                v-if="entry.protected"
                                class="tag is-warning is-light is-small ranking-snapshot__badge"
                                :title="translate('page.public_brackets.ranking_protected_seed')"
                            >
                                <i class="fas fa-shield-alt"></i>
                            </span>
                        </div>
                        <div class="is-flex is-align-items-center mt-2 is-gap-1">
                            <CompetitorAvatar :first-name="entry.firstName" :last-name="entry.lastName" :photo-url="entry.photoUrl" />
                            <p class="has-text-weight-semibold clarity-mask">{{ entry.firstName }} {{ entry.lastName }}</p>
                        </div>
                        <p class="is-size-7 mt-1">
                            <span class="has-text-grey-light">{{ translate("common.academy") }}: </span>
                            <span class="has-text-white">{{ entry.academy }}</span>
                        </p>
                        <p class="is-size-7 mt-1">
                            <span class="has-text-grey-light"> {{ translate("page.public_brackets.ranking_score") }}: </span>
                            <span v-if="entry.score !== null" class="has-text-white has-text-weight-bold">
                                {{ formatScore(entry.score) }}
                            </span>
                            <span v-else class="has-text-white">-</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import { PublicBracket, RankingMetadataEntry } from "../Api/brackets";
    import { BracketSeedingTypeEnum, BracketType, BracketTypeEnum } from "../Common/types";
    import { autoConvertToTimezoneLocalized } from "../Common/dateTimeUtils";
    import { translate } from "../Common/translations";
    import CompetitorAvatar from "../Common/Competitors/CompetitorAvatar.vue";

    const props = defineProps<{
        bracket: PublicBracket;
    }>();

    function formatScore(score: number): string {
        return score.toFixed(2);
    }

    const rankingDate = computed(() => {
        if (!props.bracket.createdAt) {
            return "";
        }
        return autoConvertToTimezoneLocalized(props.bracket.createdAt, "dd LLL yyyy");
    });

    const rankingEntries = computed<RankingMetadataEntry[]>(() => {
        return Array.isArray(props.bracket.metadata?.ranking) ? props.bracket.metadata.ranking : [];
    });

    const RANKING_SUPPORTED_TYPES = new Set<BracketType>([
        BracketTypeEnum.SingleElimination,
        BracketTypeEnum.SingleElimination3rdPlacePlayoff,
        BracketTypeEnum.DoubleEliminationTwo3rdPlaces,
        BracketTypeEnum.DoubleEliminationOne3rdPlace,
        BracketTypeEnum.TwoPoolsTwo3rdPlaces,
        BracketTypeEnum.TwoPoolsOne3rdPlace,
    ]);

    const showRankingTable = computed(() => {
        return (
            RANKING_SUPPORTED_TYPES.has(props.bracket.type) &&
            props.bracket.seedingType === BracketSeedingTypeEnum.ByRankingAndAcademy &&
            !props.bracket.manualChange &&
            rankingEntries.value.some(ranking => ranking.score !== null)
        );
    });

    defineExpose({ showRankingTable });
</script>

<style scoped>
    .ranking-snapshot {
        border-top: 2px solid #2d3f50;
        padding-top: 1.25rem;
    }

    .ranking-snapshot__header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .ranking-snapshot__table tbody tr.ranking-snapshot__row--protected {
        background-color: var(--theme-primary-15);
        border-left: 3px solid var(--bulma-primary);
    }

    .ranking-snapshot__position {
        width: 2.5rem;
        font-variant-numeric: tabular-nums;
    }

    .ranking-snapshot__badge {
        vertical-align: middle;
        font-size: 0.65rem;
    }
</style>
